// src/pages/meeting/NoticePage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getNotices, deleteNotice } from "../../api/meeting";
import { Search, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { formatDate } from "../../utils/date";

const NoticePage = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // 검색 관련
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);

  const [searchTrigger, setSearchTrigger] = useState(0);

  // 데이터 로드
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getNotices({
        project: localStorage.getItem("projectId"),
        q,
        search_field: searchField,
        page,
        page_size: pageSize,
      });
      setItems(res.data.results ?? []);
      setTotal(res.data.total ?? 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchTrigger, page, pageSize]);

  const rows = useMemo(() => items ?? [], [items]);
  const totalPages = Math.ceil(total / pageSize) || 1;

  const handleSearch = () => {
    setPage(1);
    setSearchTrigger((x) => x + 1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto max-w-lg px-4 pt-6 pb-8">
        {/* 헤더: 제목 + 새 공지 버튼 */}
        <header className="mb-5">
          <div className="flex items-center justify-between">
            <h1 className="text-[28px] font-bold tracking-tight text-slate-900">
              공지사항
            </h1>
            <button
              onClick={() => navigate(`../new`)}
              className="flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-[13px] font-semibold text-white shadow-sm active:scale-[0.97] transition-transform"
            >
              <Plus size={16} strokeWidth={2.5} />
              새 공지
            </button>
          </div>
          <p className="mt-1 text-[15px] text-slate-500">
            총 {total}건의 공지사항
          </p>
        </header>

        {/* 검색 영역 */}
        <section className="mb-4 space-y-2">
          {/* 검색 입력창 */}
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <Search size={18} className="text-slate-400 flex-shrink-0" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="검색어를 입력하세요"
              className="flex-1 min-w-0 bg-transparent text-[15px] text-slate-900 placeholder-slate-400 outline-none"
            />
          </div>

          {/* 필터 + 검색 버튼 */}
          <div className="flex items-center gap-2">
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[14px] text-slate-700 outline-none shadow-sm"
            >
              <option value="title">제목</option>
              <option value="context">내용</option>
              <option value="created_by">작성자</option>
            </select>
            <button
              onClick={handleSearch}
              className="rounded-xl bg-slate-900 px-5 py-2.5 text-[14px] font-semibold text-white shadow-sm active:scale-[0.97] transition-transform"
            >
              검색
            </button>
          </div>
        </section>

        {/* 목록 */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-[15px] text-slate-400">불러오는 중...</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-[15px] text-slate-400">공지사항이 없습니다.</p>
          </div>
        ) : (
          <section className="space-y-2">
            {rows.map((row) => (
              <button
                key={row.id}
                onClick={() =>
                  navigate(`/project/${projectId}/meeting/notice/${row.dns}/view`)
                }
                className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm active:scale-[0.98] transition-transform"
              >
                <p className="text-[15px] font-semibold text-slate-900 leading-snug line-clamp-2">
                  {row.title}
                </p>
                <div className="mt-2 flex items-center gap-2 text-[13px] text-slate-500">
                  <span>{row.created_by || "작성자 미상"}</span>
                  <span className="text-slate-300">·</span>
                  <span>{formatDate(row.created_at)}</span>
                </div>
              </button>
            ))}
          </section>
        )}

        {/* 페이지네이션 */}
        {!loading && rows.length > 0 && (
          <footer className="mt-6 flex items-center justify-center gap-4">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-600 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.95]"
            >
              <ChevronLeft size={20} />
            </button>

            <div className="flex items-center gap-1 text-[14px]">
              <span className="font-semibold text-slate-900">{page}</span>
              <span className="text-slate-400">/</span>
              <span className="text-slate-500">{totalPages}</span>
            </div>

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-600 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.95]"
            >
              <ChevronRight size={20} />
            </button>
          </footer>
        )}
      </main>
    </div>
  );
};

export default NoticePage;
